<template>
  <ProgramLayout
      :active-tab="activeTab"
      :on-save="handleSave"
      :on-weblink-action="handleWeblinkAction"
      :on-settings-action="() => settingsModalVisible = true"
      :on-assign-action="() => assignModalVisible = true"
      :action-text="activeTab === 2 ? this.$t('save_button') : this.$t('next')"
      :on-copy-program="onCopyAction"
      :on-preview-action="()=> previewModalVisible = true"
      :on-tour="onTour"
      :on-log-action="showLogModal"
      :on-back="handleBack"
  >
    <div class="add-program-wrapper">
      <Transition name="slide-fade-rtl">
        <div id="th-step-9" class="sidebar" v-if="activeTab === 1 || activeTab === 2">
          <h3 class="welcome-title">{{$t('hello_user', {name: getName})}} ☀️</h3>
          <h3 class="welcome-text">{{$t('create_new_programs')}}</h3>

          <draggable
              v-model="programData.modules"
              item-key="order"
          >
            <template  #item="{element, index}" :key="element.order + forceRerender">
              <div class="module-block">
                <div class="module-block-header" @click="() => {
                  if (activeModuleCard === index) {
                    activeModuleCard = -1;
                  } else {
                    activeModuleCard = index;
                  }
                }">
                  <div class="module-block-icon">
                    <img src="@/assets/images/settingsOutline.svg" @click="() => {
                      moduleSettingsIndex = index;
                      moduleSettingsVisible = true;
                    }"/>
                    <input class="module-name-input" v-model="element.name" :placeholder="$t('module_name')"/>
                  </div>
                  <img src="@/assets/images/arrowdown.svg" class="module-block-arrow" :class="{'rotate': activeModuleCard === index}"/>
                </div>

                <Transition name="slide-up">
                  <div v-if="activeModuleCard === index" class="module-block-body">
                    <div class="module-block-list">
                      <div
                          class="module-name-row"
                          :class="{'active': checkActiveModule(module.order)}"
                          v-for="module in element.subModules"
                          @click="setActiveSubModule(module.order)"
                          :key="module.order + forceRerender"
                      >
                        <span class="module-name">{{module.name}}</span>
                        <span class="module-step">{{$t('step_count', {stepCount: module.parts.length})}}</span>
                      </div>
                    </div>
                    <button class="primary-button-outline w-100 mt-3" @click="() => addSubModule(element)">{{$t('add_sub_module')}}</button>
                  </div>
                </Transition>
              </div>
            </template>
          </draggable>

          <button class="primary-button-outline w-100 mt-3" @click="addModule()">{{ $t('add_module') }}</button>
        </div>
      </Transition>

      <div class="content" ref="content">
        <div class="program-tab-wrapper no-text-select">
          <div class="program-tab-panel-left">
            <div id="th-step-5" class="program-tab-pane" :class="{'active': activeTab === 0}" @click="() => changeTab(0)">
              {{$t('program_info_header')}}
            </div>

            <div id="th-step-6" class="program-tab-pane" :class="{'active': activeTab === 1}" @click="() => changeTab(1)">
              {{$t('program_content_header')}}
            </div>

            <div id="th-step-7" class="program-tab-pane" :class="{'active': activeTab === 2}" @click="() => changeTab(2)">
              {{$t('program_flow')}}
            </div>
          </div>
        </div>

        <div class="content-wrapper" v-if="activeTab === 0">
          <div class="content-left">
            <div class="card-wrapper">
              <div class="title-card-border"></div>
              <input class="title-input" :placeholder="$t('add_your_program_name')" v-model="programData.name" />
              <input class="description-input" :placeholder="$t('explanation_of_the_program')" v-model="programData.description" />
            </div>

            <div class="card-wrapper">
              <div class="card-row no-text-select">
                <div class="row-50">
                  <span class="input-title">
                    {{ $t('expert_title') }}
                  </span>

                  <div class="dropdown" @click="toggleProgramManagerDropdown" v-click-outside="hideProgramManagerDropdown">
                    <span v-if="!programData.expertIds || programData.expertIds.length <= 0">{{ $t('select_expert') }}</span>
                    <span v-else>
                      {{$t('expert_selected_count', {expertCount: programData.expertIds.length})}}
                    </span>
                    <img src="@/assets/images/arrowdown.svg" />

                    <div class="dropdown-content" v-if="programManagerDropdown">
                      <div class="programManager-list scroll-design">
                        <div class="programManager-item" v-for="expert in expertsData" @click="toggleExpert(expert.id)">
                          <img v-if="programData.expertIds && programData.expertIds.includes(expert.id)" class="check" src="@/assets/images/checkboxChecked.svg" />
                          <img v-else class="check" src="@/assets/images/checkbox.svg" />
                          <span>{{ expert.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row-50">
                  <span class="input-title">
                    {{ $t('tags_title') }}
                  </span>

                  <div class="dropdown" @click="toggleTagsDropdown" v-click-outside="hideTagsDropdown">
                    <span v-if="tagsData.filter(tag => tag.selected).length === 0">{{$t('select_tags_question')}}</span>
                    <span v-else>
                      {{$t('selected_count', {selectedCount: tagsData.filter(tag => tag.selected).length})}}
                    </span>
                    <img src="@/assets/images/arrowdown.svg" />

                    <div class="dropdown-content" v-if="tagsDropdown">
                      <div class="tags-list scroll-design">
                        <div class="programManager-item" v-for="tag in tagsData" @click="toggleTag(tag.id)">
                          <img v-if="tag.selected" class="check" src="@/assets/images/checkboxChecked.svg" />
                          <img v-else class="check" src="@/assets/images/checkbox.svg" />
                          <span>{{ tag.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <span class="input-title pt-4">
                {{ $t('what_people_will_learn') }}
              </span>
              <quill-editor theme="snow" v-model:content="programData.learnDescription" content-type="html" :placeholder="$t('please_what_people_will_learn')"></quill-editor>
            </div>

            <div class="bottom-actions">
              <button class="dark-button" @click="changeTab(1)">
                {{ $t('continue') }}
              </button>
            </div>
          </div>

          <div class="content-right"></div>
        </div>

        <div class="content-wrapper" v-if="activeTab === 1">
          <div class="content-left">
            <div class="card-wrapper">
              <div class="title-card-border"></div>
              <input class="title-input" :placeholder="$t('add_module_name_here')" v-model="getActiveSubModule.name"/>
            </div>

            <div class="port-list">
              <div v-for="(part,index) in orderedParts">
                <Part
                    v-if="!isQuestion(part.content.type)"
                    :key="part.order + forceRerender"
                    :data="part"
                    :part-update="handlePartUpdate"
                    :on-delete="deletePart"
                    :on-change-position="changeOrder"
                    :on-duplicate-question="duplicatePart"
                    :test-list="testList"
                    v-model:deleteList="deleteList"
                    :ai-socket="aiSocket"
                    :index="index"
                />

                <Question
                    v-else
                    :key="part.order + forceRerender"
                    :data="part"
                    :is-part-of-program="true"
                    :question-update="handlePartUpdate"
                    :on-delete="deletePart"
                    :on-change-position="changeOrder"
                    :on-duplicate-question="duplicatePart"
                    :test-list="testList"
                    :tag-list="tagsData"
                    v-model:deleteList="deleteList"
                    :ai-socket="aiSocket"
                />
              </div>
            </div>

            <div class="bottom-actions">
              <button class="dark-button"  @click="addPart">
                <img src="@/assets/images/plus.svg"/>
                {{$t('add_part')}}
              </button>
            </div>
          </div>

          <div id="th-step-11" class="content-right">
            <div class="card-wrapper">
              <h3>{{ $t('module_details') }}</h3>

              <draggable
                  v-model="getActiveSubModule.parts"
                  item-key="order"
                  @end="handleDragEnd"
              >
                <template  #item="{element}" :key="element.order + forceRerender">
                  <div class="question-block-row" >
                    <img src="@/assets/images/drag.svg" />
                    <div class="question-block" :class="validateProgramPart(element).type">
                      <div class="question-number">
                        <img class="check-icon" src="@/assets/images/checkCircle.svg">
                        <img class="error-icon" src="@/assets/images/errorCircle.svg">
                        {{$t('step')}} {{ element.order }}: {{ element.name ? element.name.substr(0, 30) : '' }}
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>

            </div>
          </div>
        </div>
        <div class="content-wrapper no-text-select" v-if="activeTab === 2">
          <div class="content-left">
            <div id="th2-step-11" class="dnd-flow" @drop="onDrop">
              <VueFlow
                  @dragover="onDragOver"
                  @dragleave="onDragLeave"
                  v-model:nodes="getActiveSubModule.conditions.nodes"
                  v-model:edges="getActiveSubModule.conditions.edges"
                  :default-edge-options="{
                    animated: false,
                    type: 'smoothstep'
                  }"
                  @edge-click="handleEdgeClick"
              >
                <DropzoneBackground
                    :style="{backgroundColor: isDragOver ? '#e7f3ff' : 'transparent',transition: 'background-color 0.2s ease',}"
                >
                  <p v-if="isDragOver">{{$t('drop_here')}}</p>
                </DropzoneBackground>

                <template #node-question="nodeProps">
                  <QuestionNode v-bind="nodeProps" />
                </template>

                <template #node-one-way-question="nodeProps">
                  <OneWayQuestion v-bind="nodeProps" />
                </template>

                <template #node-number-question="nodeProps">
                  <NumberQuestionNode
                      v-bind="nodeProps"
                      :on-select="handleNumberQuestionSelect"
                  />
                </template>

                <template #node-start="nodeProps">
                  <StartNode v-bind="nodeProps" />
                </template>

                <template #node-end="nodeProps">
                  <EndNode v-bind="nodeProps" />
                </template>

                <template #node-and="nodeProps">
                  <AndNode v-bind="nodeProps" :refresh-internals="refreshInternals"  />
                </template>

                <template #node-or="nodeProps">
                  <OrNode v-bind="nodeProps" :refresh-internals="refreshInternals" />
                </template>

                <template #node-answer-condition="nodeProps">
                  <AnswerConditionNode
                      v-bind="nodeProps"
                      :test-list="testList"
                      :on-select="handleAnswerConditionSelect"
                  />
                </template>

                <template #node-test-score-condition="nodeProps">
                  <TestScoreCondition
                      v-bind="nodeProps"
                      :test-list="testList"
                      :on-select="handleScoreConditionSelect"
                  />
                </template>

                <template #node-assign="nodeProps">
                  <AssignNode
                      v-bind="nodeProps"
                      :test-list="testList"
                      :on-select="handleAssignSelect"
                  />
                </template>

                <template #node-notification="nodeProps">
                  <NotificationNode
                      v-bind="nodeProps"
                      :notification-list="notificationList"
                      :notification-select="handleNotificationSelect"
                  />
                </template>

                <template #node-part="nodeProps">
                  <PartNode v-bind="nodeProps" />
                </template>

                <template #node-grading-repeat="nodeProps">
                  <GradingRepeatNode
                      v-bind="nodeProps"
                      :module-list="this.programData.modules"
                      :grading-select="handleGradingSelect"
                  />
                </template>


                <template #node-go-to="nodeProps">
                  <GoToNode
                      v-bind="nodeProps"
                      :module-list="this.programData.modules"
                      :on-select="handleGoToSelect"
                  />
                </template>
              </VueFlow>

            </div>
          </div>

          <div class="content-right">
            <div class="card-wrapper">
              <h3>{{$t('nodes')}}</h3>

              <div id="th2-step-10" class="node-grid">
                <div class="node-button" :draggable="true" @dragstart="onDragStart($event, 'answer-condition')">
                  <img src="@/assets/images/testOutline.svg" />
                  <span>{{$t('answer')}}</span>
                </div>

                <div class="node-button" :draggable="true" @dragstart="onDragStart($event, 'test-score-condition')">
                  <img src="@/assets/images/medalOutline.svg">
                  <span>{{$t('score')}}</span>
                </div>


                <div class="node-button" :draggable="true" @dragstart="onDragStart($event, 'notification')">
                  <img src="@/assets/images/notificationOutline.svg">
                  <span>{{ $t('notification') }}</span>
                </div>


                <div class="node-button" :draggable="true" @dragstart="onDragStart($event, 'assign')">
                  <img src="@/assets/images/linkCircleOutline.svg">
                  <span>{{ $t('assign') }}</span>
                </div>

                <div class="node-button" :draggable="true" @dragstart="onDragStart($event, 'grading-repeat')">
                  <img src="@/assets/images/repeatOutline.svg">
                  <span>{{$t('grading_repeat')}}</span>
                </div>

              </div>

              <button class="flow-button" @click="showDeleteModal">{{ $t('reset_flow') }}</button>

              <button class="flow-button" @click="autoConnectNodes">{{ $t('auto_connect') }}</button>
              <ConfirmDeleteModal
                  :title="this.$t('flow_reset_alert')"
                  :message="this.$t('flow_reset_alertMessage')"
                  :is-visible="deleteModalVisible"
                  :on-action="onDeleteFlow"
                  :on-close="handleDeleteModalClose"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="scroll-wrapper">
        <button class="go-button up" @click="goUp">
          <img src="@/assets/images/chevronUp.svg" />
        </button>
        <button class="go-button down" @click="goDown">
          <img src="@/assets/images/chevronDown.svg" />
        </button>
      </div>

      <LoadingOverlay :is-loading="isLoading" />
      <v-tour name="programTour" :steps="programTourSteps" :options="labels"></v-tour>
      <v-tour name="program2Tour" :steps="program2TourSteps" :options="labels"></v-tour>
    </div>

    <WeblinkModal
        :visible="weblinkModalVisible"
        :on-hide="hideWeblinkModal"
        :program-id="programData?.programId"
        :old-short-code="programData?.shortCode"
        :package-limit-web-link="programData?.packageLimitWebLink"
        :short-code-click-count="programData?.shortCodeClickCount"
    />

    <ProgramSettingsModal
        :visible="settingsModalVisible"
        :on-hide="hideSettingsModal"
        :settings="programData" />

    <ProgramPreviewModal
        :visible="previewModalVisible"
        :on-hide="hidePreviewModal"
        :program-data="programData"
    />

    <ProgramTestsModal
        :visible="assignModalVisible"
        :on-hide="() => assignModalVisible = false"
        :program-id="programData?.programId"
        :assigned-id="programData.testId"
        :weight-list="programWeights"
        :on-select="handleAssignTestSelect"
    />

    <ModuleSettingsModal
        :visible="moduleSettingsVisible"
        :on-hide="() => {
          moduleSettingsVisible = false;
          moduleSettingsIndex = -1;
        }"
        :module="programData.modules[moduleSettingsIndex]"
        :on-delete-module="handleDeleteModule"
        :on-delete-sub-module="handleDeleteSubModule"
    />

    <LogModal
        :visible="isLogModalVisible"
        :on-hide="hideLogModal"
        :data="logData"
        :total-items="logMeta.total"
        :current-page="logMeta.currentPage"
        :per-page="logMeta.perPage"
        :on-page-change="handleLogPageChange"
    />

    <UnsavedChangesModal
        :is-visible="showUnsavedModal"
        :is-loading="isLoading"
        @save="handleUnsavedSave"
        @discard="handleUnsavedDiscard"
    />
  </ProgramLayout>
</template>

<script>
import ProgramLayout from "@/components/layouts/ProgramLayout.vue";
import Question from "@/components/Question.vue";
import Part from "@/components/Part.vue";
import commonService from "@/service/commonService";
import programService from "@/service/programService";
import {
  VueSpinnerCore
} from 'vue3-spinners'
import draggable from 'vuedraggable'
import testService from "@/service/testService";
import {deepClone, handleErrorResponse, isQuestion, uuidv4, validateProgramPart} from "@/utils/utils";
import ProgramSettingsModal from "@/components/modals/ProgramSettingsModal.vue";
import ProgramTestsModal from "@/components/modals/ProgramTestsModal.vue";
import ModuleSettingsModal from "@/components/modals/ModuleSettingsModal.vue";
import useDragAndDrop from '@/components/condition-flow/useDnD'
import { VueFlow, useVueFlow } from '@vue-flow/core'
import PartNode from "@/components/condition-flow/nodes/PartNode.vue";
import DropzoneBackground from "@/components/condition-flow/DropzoneBackground.vue";
import StartNode from "@/components/condition-flow/nodes/StartNode.vue";
import AnswerConditionNode from "@/components/condition-flow/nodes/AnswerConditionNode.vue";
import QuestionNode from "@/components/condition-flow/nodes/QuestionNode.vue";
import EndNode from "@/components/condition-flow/nodes/EndNode.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import NotificationNode from "@/components/condition-flow/nodes/NotificationNode.vue";
import notificationContentService from "@/service/notificationContentService";
import AndNode from "@/components/condition-flow/nodes/AndNode.vue";
import OrNode from "@/components/condition-flow/nodes/OrNode.vue";
import {nextTick, ref, onMounted, onBeforeUnmount} from "vue";
import TestScoreCondition from "@/components/condition-flow/nodes/TestScoreCondition.vue";
import AssignNode from "@/components/condition-flow/nodes/AssignNode.vue";
import NumberQuestionNode from "@/components/condition-flow/nodes/NumberQuestionNode.vue";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal.vue";
import {useI18n} from "vue-i18n";
import GradingRepeatNode from "@/components/condition-flow/nodes/GradingRepeatNode.vue";
import GoToNode from "@/components/condition-flow/nodes/GoToNode.vue";
import TranslationDropdown from "@/components/TranslationDropdown.vue";
import ProgramPreviewModal from "@/components/modals/ProgramPreviewModal.vue";
import OneWayQuestion from "@/components/condition-flow/nodes/OneWayQuestion.vue";
import fileService from "@/service/fileService";
import GameCard from "@/components/GameCard.vue";
import aiSocket from "@/utils/aiSocket";
import WeblinkModal from "@/components/modals/WeblinkModal.vue";
import LogModal from "@/components/modals/LogModal.vue";
import UnsavedChangesModal from "@/components/modals/UnsavedChangesModal.vue";


export default {
  components: {
    LogModal,
    WeblinkModal,
    GameCard,
    ProgramPreviewModal,
    OneWayQuestion,
    TranslationDropdown,
    GoToNode,
    GradingRepeatNode,
    NumberQuestionNode,
    AssignNode,
    TestScoreCondition,
    OrNode,
    AndNode,
    NotificationNode,
    LoadingOverlay,
    EndNode,
    QuestionNode,
    AnswerConditionNode,
    StartNode,
    DropzoneBackground,
    PartNode,
    VueFlow,
    ModuleSettingsModal,
    ProgramTestsModal,
    ProgramSettingsModal,
    Part,
    Question,
    VueSpinnerCore,
    draggable,
    ConfirmDeleteModal,
    ProgramLayout,
    UnsavedChangesModal
  },
  data() {
    return {
      logData: [],
      allLogData: [],
      logMeta: {
        total: 0,
        currentPage: 1,
        perPage: 20
      },
      isLogModalVisible: false,
      labels: this.tourConfig,
      program2TourSteps: [
        {
          target: '#th-step-0',
          content: this.$t("programTour.step0"),
          params: {
            placement: 'bottom',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-1',
          content: this.$t("programTour.step1"),
          params: {
            placement: 'bottom',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-2',
          content: this.$t("programTour.step2"),
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '#th-step-3',
          content: this.$t("programTour.step3"),
          params: {
            placement: 'top',
            highlight: true
          }
        },
        {
          target: '#th-step-4',
          content: this.$t("programTour2.step4"),
          params: {
            placement: 'top',
            highlight: true
          }
        },
        {
          target: '#th-step-5',
          content: this.$t("programTour.step5"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-6',
          content: this.$t("programTour.step6"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-7',
          content: this.$t("programTour.step7"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-9',
          content: this.$t("programTour.step9"),
          params: {
            placement: 'left',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th2-step-10',
          content: this.$t("programTour2.step10"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th2-step-11',
          content: this.$t("programTour2.step13"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th2-step-11',
          content: this.$t("programTour2.step11"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th2-step-11',
          content: this.$t("programTour2.step12"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th2-step-11',
          content: this.$t("programTour2.step14"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
      ],
      programTourSteps: [
        {
          target: '#th-step-0',
          content: this.$t("programTour.step0"),
          params: {
            placement: 'bottom',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-1',
          content: this.$t("programTour.step1"),
          params: {
            placement: 'bottom',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-2',
          content: this.$t("programTour.step2"),
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '#th-step-3',
          content: this.$t("programTour.step3"),
          params: {
            placement: 'top',
            highlight: true
          }
        },
        {
          target: '#th-step-4',
          content: this.$t("programTour.step4"),
          params: {
            placement: 'top',
            highlight: true
          }
        },
        {
          target: '#th-step-5',
          content: this.$t("programTour.step5"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-6',
          content: this.$t("programTour.step6"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-7',
          content: this.$t("programTour.step7"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-11',
          content: this.$t("programTour.step11"),
          params: {
            placement: 'top',
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: '#th-step-9',
          content: this.$t("programTour.step9"),
          params: {
            placement: 'left',
            enableScrolling: false,
            highlight: true
          }
        },
      ],
      deleteList: [],
      aiSocket,
      deleteModalVisible: false,
      isLoading: true,
      editMode: false,
      activeLanguage: "tr",
      expertsDataLoading: true,
      activeTab: 0,
      programManagerDropdown: false,
      tagsDropdown: false,
      tagsData: [{
        id: 0,
        name: "Loading...",
        selected: false
      }],
      expertsData: [{
        id: 0,
        name: "Loading...",
        selected: false
      }],
      activeModuleCard: 0,
      activeSubModule: 1,
      previewModalVisible: false,
      settingsModalVisible: false,
      assignModalVisible: false,
      moduleSettingsVisible: false,
      moduleSettingsIndex: -1,
      weblinkModalVisible: false,
      programData: {
        name: "",
        description: "",
        shortDescription: "",
        learnDescription: "",
        expertIds: [],
        isRetry: false,
        retryTime: 0,
        startAt: null,
        finishAt: null,
        tagIds: [],
        isPublish: 0,
        type: "1",
        modules: [
          {
            name: this.$t('education'),
            subModules: [{
              name: this.$t('new_sub_module'),
              order: 1,
              parts: [{
                name: "",
                status: 1,
                order: 1,
                content: {
                  type: "welcomeText",
                  settings: {
                    drawer: false,
                    required: true,
                    other: false,
                    chat: false,
                    where: []
                  },
                }
              }],
              conditions: {
                nodes: [
                  {
                    id: 'start',
                    type: 'start',
                    position: {x: 200, y: 5},
                  },
                  {
                    id: 'end',
                    type: 'end',
                    position: {x: 400, y: 5},
                  }
                ],
                edges: [],
                lastNodePosition: {x: 200, y: 5},
              }
            }],
          },
        ],
      },
      lastOrder: 1,
      forceRerender: 0,
      testList: [],
      notificationList: [],
      programWeights: [],
      showUnsavedWarning: false,
      showUnsavedModal: false,
    }
  },
  setup() {
    const {onConnect, addEdges, updateNode, $reset, setNodes, getNodes, setEdges, updateNodeInternals} = useVueFlow();
    const {onDragOver, onDrop, onDragLeave, isDragOver} = useDragAndDrop();
    const {t} = useI18n();
    const selectedEdgeId = ref(null);

    const handleEdgeClick = (event) => {
      selectedEdgeId.value = event.edge.id;
    };
    const handleDeleteKeyPress = (event) => {
      if (event.key === 'Delete' && selectedEdgeId.value) {
        setEdges((prevEdges) => {
          return prevEdges.filter((edge) => edge.id !== selectedEdgeId.value);
        });
        selectedEdgeId.value = null;
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', handleDeleteKeyPress);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleDeleteKeyPress);
    });


    const checkNodeType = (nodeId) => {
      const nodes = getNodes.value;
      const node = nodes.find(node => node.id === nodeId);
      return node?.data?.content?.type || node?.type || '';
    }

    onConnect((params) => {
      if (params.target === 'end' && (checkNodeType(params.source) !== 'success' && checkNodeType(params.source) !== 'failed' && checkNodeType(params.source) !== 'go-to')) {
        $snackbar.add({
          text: t('invalid_end_connection'),
          type: "warning",
        });
        return;
      }

      const newEdge = {
        ...params,
        id: `edge-${uuidv4()}`,
      };
      addEdges([newEdge]);
    });

    const resetFlow = async () => {
      await nextTick();
      $reset();
    };

    return {
      resetFlow,
      onDragOver,
      onDrop,
      onDragLeave,
      isDragOver,
      updateNodeInternals,
      updateNode,
      setNodes,
      setEdges,
      onConnect,
      addEdges,
      handleEdgeClick,
    };
  },
  async mounted() {
    const routeParams = this.$route.params;
    this.isLoading = true;

    const promises = [
      commonService.getTags().then((res) => {
        this.tagsData = res.data.data.tag.map((tag) => {
          return {
            id: tag.id,
            name: tag.name,
            selected: false
          }
        });
      }),
      commonService.getExperts().then((res) => {
        this.expertsData = res.data.data.users.map((user) => {
          return {
            id: user.id,
            name: user.name,
            selected: false
          }
        });
        this.expertsDataLoading = false;
      }),
      testService.getTestList({types: [2, 3]}).then((response) => {
        this.testList = response.data.data;
      }).catch(() => {
        this.$snackbar.add({
          text: this.$t('fetching_test_list_failed'),
          type: "error",
        });
      }),
      notificationContentService.getNotificationContentList(true).then((response) => {
        this.notificationList = response.data.data;
      }).catch(() => {
        this.$snackbar.add({
          text: this.$t('fetching_notification_list_failed'),
          type: "error",
        });
      })
    ];

    if (routeParams.id !== "new") {
      promises.push(this.handleGetDetail());
    }

    await Promise.all(promises);

    this.isLoading = false;
    this.$nextTick(() => {
      this.showUnsavedWarning = false;
    });

    aiSocket.connect();

    const userId = JSON.parse(localStorage.getItem('user'))?.userId;
    const token = localStorage.getItem('token');
    if (userId && token) {
      aiSocket.joinGenvidRoom(userId, token);
    }
  },
  unmounted() {
    aiSocket.disconnect();
  },
  watch: {
    "programData.description": function (newVal, oldVal) {
      if (!newVal) {
        return;
      }
      this.programData.shortDescription = newVal;
    },
    programData: {
      handler(newVal, oldVal) {
        if (this.isLoading) return;
        this.showUnsavedWarning = true;
      },
      deep: true
    },
  },
  methods: {
    getLogs(page = 1) {
      const routeParams = this.$route.params;

      if (routeParams.id !== 'new') {
        programService.getDetailLogs(this.$i18n.locale, routeParams.id).then((res) => {
          this.allLogData = res.data.data;

          this.logMeta = {
            total: this.allLogData.length,
            currentPage: page,
            perPage: 20
          };

          this.paginateLogs(page);
        }).catch((err) => {
          handleErrorResponse(err, this.$snackbar);
        });
      }
    },

    handleLogPageChange(page) {
      if (this.logMeta.currentPage === page) return;
      this.logMeta.currentPage = page;
      this.paginateLogs(page);
    },

    paginateLogs(page) {
      const start = (page - 1) * this.logMeta.perPage;
      const end = start + this.logMeta.perPage;
      this.logData = this.allLogData.slice(start, end);
    },

    showLogModal() {
      const routeParams = this.$route.params;
      if (routeParams.id === 'new') {
        this.$snackbar.add({
          text: this.$t('save_program_first'),
          type: "warning",
        });
        return;
      }
      this.isLogModalVisible = true;
      this.logMeta.currentPage = 1;
      this.getLogs(1);
    },

    hideLogModal() {
      this.isLogModalVisible = false;
    },

    onTour() {
      if (this.activeTab === 1) {
        this.$tours['programTour'].start()
      } else if (this.activeTab === 2) {
        this.$tours['program2Tour'].start()
      }
    },
    validateProgramPart,
    isQuestion,
    handleGetDetail() {
      const routeParams = this.$route.params;
      this.isLoading = true;
      this.editMode = true;
      this.activeTab = 1;

      return programService.getProgramDetail(routeParams.id, this.activeLanguage).then((response) => {
        this.handleGetModules(response.data.data);
      }).catch(() => {
        this.$snackbar.add({
          text: this.$t('fetching_program_failed'),
          type: "error",
        });
      });
    },
    showDeleteModal() {
      this.deleteModalVisible = true;
      event.stopPropagation();
    },
    handleDeleteModalClose() {
      this.deleteModalVisible = false;
    },
    onDeleteFlow() {
      this.getActiveSubModule.conditions.nodes = [
        {
          id: 'start',
          type: 'start',
          position: {x: 200, y: 5},
          deletable: false,
        },
        {
          id: 'end',
          type: 'end',
          position: {x: 400, y: 5},
          deletable: false,
        }
      ];

      if (this.getActiveSubModule?.conditions?.edges) {
        this.getActiveSubModule.conditions.edges = [];
        this.setEdges([]);
      }

      this.updateNodes();
      this.handleDeleteModalClose();
      this.arrangeNodesOnly();

    },
    goDown() {
      const container = this.$refs.content;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    },
    goUp() {
      const container = this.$refs.content;
      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    onDragStart(event, type) {
      const {onDragStart} = useDragAndDrop();
      onDragStart(event, type);
    },
    handleGetModules(data) {
      const tempData = data;

      if (tempData.modules.length > 0) {
        if (tempData.tags.length > 0) {
          this.tagsData.forEach(tag => {
            tempData.tags.forEach(programTag => {
              if (tag.id === programTag.tagId) {
                tag.selected = true;
              }
            })
          });
        }

        let lastOrder = 1;
        tempData.modules.forEach((module) => {
          module.subModules.forEach((subModule, index) => {
            subModule.order = lastOrder;
            lastOrder++;

            subModule.conditions?.nodes?.map(node => {
              node.position = {
                x: parseInt(node.position.x),
                y: parseInt(node.position.y),
              }
            });
          });
        });

        this.lastOrder = tempData.modules.flatMap(module => module.subModules).length;

        this.programData = tempData;
        this.updateNodes();
        this.forceRerender++;
      }

      if (data.type === '1') {
        programService.fetchProgramWeights(data.programId).then((response) => {
          this.programWeights = response.data.data;
        }).catch(() => {
          this.$snackbar.add({
            text: this.$t('fetching_program_weights_failed'),
            type: "error",
          });
        });
      }
    },
    changeTab(tab) {
      this.activeTab = tab;

      if (tab === 2) {
        this.updateNodes();

        if (this.showUnsavedWarning) {
          this.$snackbar.add({
            text: this.$t('there_unsaved_changes_program'),
            type: "warning",
          });
        }
      }
    },
    toggleProgramManagerDropdown() {
      this.programManagerDropdown = !this.programManagerDropdown;
      this.tagsDropdown = false;
    },
    hideProgramManagerDropdown() {
      this.programManagerDropdown = false;
    },
    toggleTagsDropdown() {
      this.tagsDropdown = true;
      this.programManagerDropdown = false;
      event.stopPropagation();
    },
    handleWeblinkAction() {
      if (!this.programData.isPublish) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('unpublished_program_weblink_warning')
        });
        return;
      }
      this.weblinkModalVisible = true;
    },
    hideWeblinkModal() {
      this.weblinkModalVisible = false;
    },
    hideTagsDropdown() {
      this.tagsDropdown = false;
    },
    hideSettingsModal() {
      this.settingsModalVisible = false;
    },
    hidePreviewModal() {
      this.previewModalVisible = false;
    },
    handleAssignTestSelect(testId) {
      this.assignModalVisible = false;
      this.programData.testId = testId;
    },
    handleDeleteModule() {
      if (this.programData.modules.length === 1) {
        this.$snackbar.add({
          text: this.$t('delete_last_module'),
          type: "warning",
        });
        return;
      }

      const tempModules = deepClone(this.programData.modules);

      tempModules.splice(this.moduleSettingsIndex, 1);

      this.activeModuleCard = 0;
      this.activeSubModule = tempModules[0].subModules[0].order;
      this.programData.modules = tempModules;
      this.moduleSettingsVisible = false;
      this.moduleSettingsIndex = -1;
    },
    handleDeleteSubModule(sub) {
      const module = this.programData.modules[this.moduleSettingsIndex];

      if (module.subModules.length === 1) {
        this.$snackbar.add(
            {
              text: this.$t('least_one_module_required'),
              type: "warning",
            }
        )
        return;
      }

      this.activeSubModule = module.subModules.filter(s => s !== sub)[0].order;
      this.moduleSettingsVisible = false;
      this.moduleSettingsIndex = -1;
      module.subModules = module.subModules.filter(s => s !== sub);
    },
    addPart() {
      try {
        const foundSubModuleParts = this.programData.modules
            .flatMap(module => module.subModules)
            .filter(subModule => subModule.order === this.activeSubModule)[0]
            .parts;

        foundSubModuleParts.push(
            {
              name: "",
              status: 1,
              order: foundSubModuleParts.length + 1,
              content: {
                type: "welcomeText",
                settings: {
                  drawer: false,
                  required: true,
                  other: false,
                  chat: false,
                  where: []
                },
              }
            }
        );
      } catch (e) {
      }
    },
    addModule() {
      this.lastOrder++;
      this.programData.modules.push({
        name: "New Module (" + (this.programData.modules.length + 1) + ")",
        subModules: [{
          name: "New Module",
          order: this.lastOrder,
          parts: [{
            name: "",
            status: 1,
            order: 1,
            content: {
              type: "welcomeText",
              settings: {
                drawer: false,
                required: true,
                other: false,
                chat: false,
                where: []
              },
            }
          }],
        }],
      });
      this.forceRerender++;
    },
    addSubModule(rootModule) {
      this.lastOrder++;
      rootModule.subModules.push({
        name: "New Sub Module (" + (rootModule.subModules.length + 1) + ")",
        order: this.lastOrder,
        parts: [{
          name: "",
          status: 1,
          order: 1,
          content: {
            type: "welcomeText",
            settings: {
              drawer: false,
              required: true,
              other: false,
              chat: false,
              where: []
            },
          },
          conditions: {
            nodes: [],
            edges: [],
            lastNodePosition: {x: 200, y: 5},
          }
        }]
      })
      this.forceRerender++;
    },
    handlePartUpdate(part) {
      const foundSubModule = this.programData.modules
          .flatMap(module => module.subModules)
          .filter(subModule => subModule.order === this.activeSubModule)[0];

      const foundPart = foundSubModule.parts.find(
          (p) => p.order === part.order
      );

      if (foundPart) {
        const index = foundSubModule.parts.indexOf(foundPart);
        foundSubModule.parts.splice(index, 1, part);
      }
    },
    toggleTag(id) {
      if (id === 0) {
        return;
      }

      const foundTag = this.tagsData.find(
          (tag) => tag.id === id
      );

      if (foundTag) {
        foundTag.selected = !foundTag.selected;
      }

      this.programData.tagIds = this.tagsData.filter(tag => tag.selected).map(tag => tag.id);
    },
    toggleExpert(id) {
      if (id === 0) {
        return;
      }

      if (this.programData.expertIds) {
        this.programData.expertIds.includes(id) ? this.programData.expertIds.splice(this.programData.expertIds.indexOf(id), 1) : this.programData.expertIds.push(id);
      } else {
        this.programData.expertIds = [id];
      }

      event.stopPropagation();
    },
    handleSave() {
      let allFalse = this.uploadStatus.every(status => {
        return Object.values(status)[0] === false;
      });
      if (allFalse) {

        if (this.activeTab === 0) {
          this.activeTab = 1;
          return;
        } else if (this.activeTab === 1) {
          this.activeTab = 2;
          this.updateNodes();

          if (this.showUnsavedWarning) {
            this.$snackbar.add({
              text: this.$t('program_finish_error'),
              type: "warning",
            });
          }

          return;
        }

        if (this.editMode) {
          this.updateProgram();
        } else {
          this.addProgram();
        }
        for (let id of this.deleteList) {
          if (id !== undefined && id !== null) {
            fileService.deleteFile(id);
          }
        }
        this.deleteList = [];
      } else {
        this.$snackbar.add({
          text: "Wait for files to be uploaded",
          type: "error",
        });
      }
    },
    async activateAllSubModules() {
      let originalActiveSubModule = this.activeSubModule;
      let allSubModules = this.programData.modules.flatMap(module => module.subModules);

      for (let subModule of allSubModules) {
        this.setActiveSubModule(subModule.order);
        this.updateNodes();
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      this.setActiveSubModule(originalActiveSubModule);
      await new Promise(resolve => setTimeout(resolve, 100));
    },
    async getSaveData() {
      await this.activateAllSubModules();
      const tempData = deepClone(this.programData);

      tempData.modules.forEach((module) => {
        module.subModules.forEach((subModule) => {
          if (subModule.conditions?.nodes?.length > 0) {
            subModule.conditions.nodes = subModule.conditions.nodes.map(node => {
              if (!node) return null;
              if (node.type === 'answer-condition' || node.type === 'test-score-condition' || node.type === 'assign' || node.type === 'notification' || node.type === 'number-question' || node.type === 'grading-repeat' || node.type === 'go-to') {
                return {
                  id: node.id,
                  type: node.type,
                  position: node.position,
                  data: {
                    condition: node?.data?.condition,
                    score: node?.data?.score,
                    testId: node?.data?.testId,
                    questionId: node?.data?.questionId,
                    option: node?.data?.option,
                    notificationId: node?.data?.notificationId,
                    dimensionalId: node?.data?.dimensionalId,
                    id: node?.data?.id,
                    name: node?.data?.name,
                    type: node?.data?.type,
                    subModuleId: node?.data?.subModuleId,
                  },
                };
              } else {
                return {
                  id: node.id,
                  type: node.type,
                  position: node.position,
                };
              }
            });
          }

          if (subModule.conditions?.edges?.length > 0) {
            const nodesMap = {};
            if (subModule.conditions.nodes) {
              subModule.conditions.nodes.forEach(node => {
                if (node) nodesMap[node.id] = node.type;
              });
            }
            subModule.conditions.edges = subModule.conditions.edges.map(edge => {
              if (!edge) return null;
              return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                targetHandle: edge.targetHandle,
                sourceType: edge.sourceNode?.type || nodesMap[edge.source] || '',
                targetType: edge.targetNode?.type || nodesMap[edge.target] || '',
              };
            });
          }
        });
      });

      return tempData;
    },
    async addProgram(redirectAfterSave = false) {
      this.isLoading = true;
      let saveData;
      try {
        saveData = await this.getSaveData();
      } catch (error) {
        this.isLoading = false;
        this.$snackbar.add({
          text: this.$t('program_save_error'),
          type: "error",
        });
        return;
      }
      programService.addProgram(saveData, this.activeLanguage).then((response) => {
        this.$snackbar.add({
          text: this.$t('program_added_successfully'),
          type: "success",
        });
        this.isLoading = false;
        this.showUnsavedWarning = false;
        if (redirectAfterSave) {
          this.$router.push('/programs');
          return;
        }
        window.location.href = "/program/" + response.data.data.programId;
      }).catch((error) => {
        this.isLoading = false;
        handleErrorResponse(error, this.$snackbar);
      }).finally(() => {
        this.showUnsavedWarning = false;
      });
    },
    async updateProgram(redirectAfterSave = false) {
      this.isLoading = true;
      let saveData;
      try {
        saveData = await this.getSaveData();
      } catch (error) {
        this.isLoading = false;
        this.$snackbar.add({
          text: this.$t('program_save_error'),
          type: "error",
        });
        return;
      }
      programService.updateProgram(saveData, this.activeLanguage).then((response) => {
        this.$snackbar.add({
          text: this.$t('program_updated_successfully'),
          type: "success",
        });
        this.showUnsavedWarning = false;
        if (redirectAfterSave) {
          this.isLoading = false;
          this.$router.push('/programs');
          return;
        }
        this.handleGetModules(response.data.data);
        this.updateNodes();
        this.isLoading = false;
        this.$nextTick(() => {
          this.showUnsavedWarning = false;
        });
      }).catch((error) => {
        this.isLoading = false;
        handleErrorResponse(error, this.$snackbar);
      });
    },
    setActiveSubModule(order) {
      this.activeSubModule = order;
      this.forceRerender++;
      this.updateNodes();
    },
    checkActiveModule(order) {
      return this.activeSubModule === order;
    },
    deletePart(part) {
      const foundSubModule = this.programData.modules
          .flatMap(module => module.subModules)
          .filter(subModule => subModule.order === this.activeSubModule)[0];

      const foundPart = foundSubModule.parts.find(
          (p) => p.order === part
      );

      if (foundPart) {
        const index = foundSubModule.parts.indexOf(foundPart);
        foundSubModule.parts.splice(index, 1);
      }
    },
    handleDragEnd() {
      const updatedParts = [...this.getActiveSubModule.parts];

      updatedParts.forEach((part, index) => {
        part.order = index + 1;
      });

      this.programData.modules
          .flatMap(module => module.subModules)
          .filter(subModule => subModule.order === this.activeSubModule)[0].parts = [...updatedParts];

      this.forceRerender++;
    },
    changeOrder(dir, orderId) {
      if (this.orderedParts.length === 1) {
        return;
      }

      if (orderId === 1 && dir === 'up') {
        return;
      }

      if (orderId === this.orderedParts.length && dir === 'down') {
        return;
      }

      const arr = this.orderedParts;
      const index = arr.findIndex(item => item.order === orderId);

      if (dir === 'up' && index > 0) {

        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];

        const tempOrder = arr[index - 1].order;
        arr[index - 1].order = arr[index].order;
        arr[index].order = tempOrder;

        this.programData.modules
            .flatMap(module => module.subModules)
            .filter(subModule => subModule.order === this.activeSubModule)[0].parts = [...arr];
      } else if (dir === 'down' && index < arr.length - 1) {

        [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];

        const tempOrder = arr[index + 1].order;
        arr[index + 1].order = arr[index].order;
        arr[index].order = tempOrder;

        this.programData.modules
            .flatMap(module => module.subModules)
            .filter(subModule => subModule.order === this.activeSubModule)[0].parts = [...arr];
      }

      this.forceRerender++;
    },
    duplicatePart(orderId) {
      const foundSubModule = this.programData.modules
          .flatMap(module => module.subModules)
          .filter(subModule => subModule.order === this.activeSubModule)[0];

      const foundPart = foundSubModule.parts.find(
          (p) => p.order === orderId
      );

      if (foundPart) {
        const index = foundSubModule.parts.indexOf(foundPart);
        const newPart = JSON.parse(JSON.stringify(foundPart));

        newPart.order = foundSubModule.parts.length + 1;
        newPart.questionId = null;
        foundSubModule.parts.splice(index + 1, 0, newPart);
      }

      this.handleDragEnd();

      this.forceRerender++;
    },
    updateNodes() {
      if (!this.getActiveSubModule) {
        return;
      }

      if (!this.getActiveSubModule.conditions) {
        this.getActiveSubModule.conditions = {
          nodes: [
            {
              id: 'start',
              type: 'start',
              position: {x: 200, y: 5},
            },
            {
              id: 'end',
              type: 'end',
              position: {x: 400, y: 5},
            }
          ],
          edges: [],
          lastNodePosition: {x: 200, y: 5},
        }
      }

      if (!this.getActiveSubModule.conditions?.nodes) {
        this.getActiveSubModule.conditions.nodes = [];
      }

      if (!this.getActiveSubModule.conditions?.edges) {
        this.getActiveSubModule.conditions.edges = [];
      }

      this.getActiveSubModule.conditions.lastNodePosition = {x: 200, y: 5};

      this.getActiveSubModule.parts.map((part) => {
        if (!part.questionId) {
          this.showUnsavedWarning = true;
          return;
        }

        if (this.getActiveSubModule.conditions.nodes && this.getActiveSubModule.conditions.nodes.find((node) => node.id === part.questionId)) {
          const node = this.getActiveSubModule.conditions.nodes.find((node) => node.id === part.questionId);
          node.data = {
            ...node.data,
            ...part,
          };
        } else {
          this.getActiveSubModule.conditions.lastNodePosition.y = parseInt(this.getActiveSubModule.conditions.lastNodePosition.y, 10) + 150;
          let currentPosition = {
            x: this.getActiveSubModule.conditions.lastNodePosition.x,
            y: this.getActiveSubModule.conditions.lastNodePosition.y
          };

          if (isQuestion(part.content.type)) {
            this.getActiveSubModule.conditions.nodes.push({
              id: part.questionId,
              type: part.content.type === 'number' || part.content.type === 'grading' || part.content.type === 'rating' ? 'number-question' : part.content.type === 'fillTheBlank' || part.content.type === 'matchType' || part.content.type === 'text' || part.content.type === 'swipe' ? 'one-way-question' : 'question',
              position: currentPosition,
              data: part,
            });
          } else {
            this.getActiveSubModule.conditions.nodes.push({
              id: part.questionId,
              type: 'part',
              label: part.name,
              position: currentPosition,
              data: part,
            });
          }
        }
      });

      if (this.getActiveSubModule.conditions.edges && this.getActiveSubModule.conditions.edges.length > 0) {
        this.getActiveSubModule.conditions.edges.map((edge) => {
          edge.type = 'smoothstep';
          edge.animated = false;
        });
      }

      this.setNodes(this.getActiveSubModule.conditions.nodes);
      this.setEdges(this.getActiveSubModule.conditions.edges);
      this.refreshInternals();
    },

    handleNotificationSelect(notificationId, nodeId) {
      this.updateNode(nodeId, {
        data: {
          notificationId: notificationId
        }
      });
    },
    handleAnswerConditionSelect(conditionData, nodeId) {
      this.updateNode(nodeId, {data: conditionData});
    },
    handleNumberQuestionSelect(questionData, nodeId) {
      this.updateNode(nodeId, {data: questionData});
    },
    handleScoreConditionSelect(conditionData, nodeId) {
      this.updateNode(nodeId, {data: conditionData});
    },
    handleAssignSelect(assignData, nodeId) {
      this.updateNode(nodeId, {data: assignData});
    },
    handleGradingSelect(questionId, nodeId) {
      this.updateNode(nodeId, {
        data: {
          questionId: questionId
        }
      });
    },
    handleGoToSelect(subId, nodeId) {
      this.updateNode(nodeId, {
        data: {
          subModuleId: subId
        }
      });
    },
    refreshInternals() {
      this.updateNodeInternals();

      setTimeout(() => {
        this.updateNodeInternals();
      }, 100);
    },
    onCopyAction() {
      this.isLoading = true;
      this.$snackbar.add({
        text: this.$t('program_copying'),
        type: "info",
      });
      programService.getCopy(this.$route.params.id).then((response) => {
        this.$snackbar.add({
          text: this.$t('program_copied_successfully'),
          type: "success",
        });
        setTimeout(() => {
          window.location.href = "/program/" + response.data.data.programId;
        }, 1000);
      }).catch((error) => {
        this.isLoading = false;
        handleErrorResponse(error, this.$snackbar);
      });
    },
    async handleLanguageChange(lang) {
      const routeParams = this.$route.params;
      this.activeLanguage = lang;

      if (routeParams.id !== "new") {
        this.handleGetDetail();
      }
      await notificationContentService.getNotificationContentList(true, this.activeLanguage).then((response) => {
        this.notificationList = response.data.data;
      }).catch(() => {
        this.$snackbar.add({
          text: this.$t('fetching_notification_list_failed'),
          type: "error",
        });
      });
    },
    async autoConnectNodes() {
      if (!this.getActiveSubModule?.conditions?.nodes) {
        this.$snackbar.add({
          text: this.$t('no_nodes_to_connect'),
          type: "warning",
        });
        return;
      }

      const nodes = [...this.getActiveSubModule.conditions.nodes];
      const newEdges = [];

      const connectableNodes = nodes
          .filter(node => node.type !== 'start' && node.type !== 'end')
          .sort((a, b) => a.position.y - b.position.y);

      const startNode = nodes.find(node => node.type === 'start');
      const endNode = nodes.find(node => node.type === 'end');

      if (!startNode || !endNode) {
        this.$snackbar.add({
          text: this.$t('start_or_end_node_missing'),
          type: "error",
        });
        return;
      }

      const generateEdgeId = () => `edge-${uuidv4()}`;

      const estimateNodeHeight = (node) => {
        const baseHeight = 100;

        switch (node.type) {
          case 'question':
            const options = node.data?.content?.options || [];
            const hasOther = node.data?.content?.settings?.other;
            return baseHeight + (options.length * 45) + (hasOther ? 45 : 0) + 60;

          case 'number-question':
          case 'one-way-question':
            return baseHeight + 20;

          case 'answer-condition':
          case 'test-score-condition':
            return baseHeight + 40;

          case 'notification':
          case 'assign':
            return baseHeight + 30;

          case 'part':
            return baseHeight + 10;

          case 'grading-repeat':
          case 'go-to':
            return baseHeight + 25;

          default:
            return baseHeight;
        }
      };

      const getNodeOutputHandles = (node) => {
        switch (node.type) {
          case 'question':

            const handles = [];
            if (node.data?.content?.options) {

              node.data.content.options.forEach((_, index) => {
                handles.push(String(index));
              });
            }

            if (node.data?.content?.settings?.other) {
              handles.push('-2');
            }

            if (handles.length === 0) {
              return ['0', '1'];
            }
            return handles;

          case 'number-question':
            return ['output', 'false'];

          case 'one-way-question':
            return ['output'];

          case 'answer-condition':
            return ['output', 'false'];

          case 'test-score-condition':
            return ['output', 'false'];

          case 'notification':
            return ['output', 'false'];

          case 'and':
          case 'or':
            return ['output'];


          case 'assign':
          case 'part':
          case 'grading-repeat':
            return ['output'];

          case 'go-to':
            return ['output'];

          case 'start':
            return ['output'];

          default:
            return ['output'];
        }
      };

      const getNodeInputHandle = (node) => {
        return 'input';
      };


      try {
        this.arrangeNodesOnly();
        if (connectableNodes.length > 0) {
          const firstNode = connectableNodes[0];
          newEdges.push({
            id: generateEdgeId(),
            source: startNode.id,
            target: firstNode.id,
            sourceHandle: 'output',
            targetHandle: getNodeInputHandle(firstNode),
            type: 'smoothstep',
            animated: false
          });
        } else {
          newEdges.push({
            id: generateEdgeId(),
            source: startNode.id,
            target: endNode.id,
            sourceHandle: 'output',
            targetHandle: 'input',
            type: 'smoothstep',
            animated: false
          });
        }

        for (let i = 0; i < connectableNodes.length; i++) {
          const currentNode = connectableNodes[i];
          const nextNode = connectableNodes[i + 1];

          const outputHandles = getNodeOutputHandles(currentNode);

          if (nextNode) {
            if (currentNode.type === 'question' && outputHandles.length > 2) {
              outputHandles.forEach(outputHandle => {
                newEdges.push({
                  id: generateEdgeId(),
                  source: currentNode.id,
                  target: nextNode.id,
                  sourceHandle: outputHandle,
                  targetHandle: getNodeInputHandle(nextNode),
                  type: 'smoothstep',
                  animated: false
                });
              });
            } else if (currentNode.type === 'question' && outputHandles.length <= 2) {
              outputHandles.forEach(outputHandle => {
                newEdges.push({
                  id: generateEdgeId(),
                  source: currentNode.id,
                  target: nextNode.id,
                  sourceHandle: outputHandle,
                  targetHandle: getNodeInputHandle(nextNode),
                  type: 'smoothstep',
                  animated: false
                });
              });
            } else {
              outputHandles.forEach(outputHandle => {
                newEdges.push({
                  id: generateEdgeId(),
                  source: currentNode.id,
                  target: nextNode.id,
                  sourceHandle: outputHandle,
                  targetHandle: getNodeInputHandle(nextNode),
                  type: 'smoothstep',
                  animated: false
                });
              });
            }
          } else {
            outputHandles.forEach(outputHandle => {
              newEdges.push({
                id: generateEdgeId(),
                source: currentNode.id,
                target: endNode.id,
                sourceHandle: outputHandle,
                targetHandle: 'input',
                type: 'smoothstep',
                animated: false
              });
            });
          }
        }
        this.getActiveSubModule.conditions.edges = newEdges;
        this.setEdges(newEdges);

        this.$snackbar.add({
          text: this.$t('nodes_connected_and_arranged'),
          type: "success",
        });

        await this.$nextTick();
        this.refreshInternals();

      } catch (error) {
        this.$snackbar.add({
          text: this.$t('error_connecting_nodes'),
          type: "error",
        });
      }
    },

    handleBack() {
      if (this.showUnsavedWarning) {
        this.showUnsavedModal = true;
      } else {
        this.$router.push('/programs');
      }
    },
    handleUnsavedSave() {
      this.showUnsavedModal = false;
      let allFalse = this.uploadStatus.every(status => {
        return Object.values(status)[0] === false;
      });
      if (!allFalse) {
        this.$snackbar.add({
          text: this.$t('wait_for_files_to_be_uploaded'),
          type: "error",
        });
        return;
      }
      if (this.editMode) {
        this.updateProgram(true);
      } else {
        this.addProgram(true);
      }
    },
    handleUnsavedDiscard() {
      this.showUnsavedModal = false;
      this.$router.push('/programs');
    },
    async arrangeNodesOnly() {
      if (!this.getActiveSubModule?.conditions?.nodes) {
        this.$snackbar.add({
          text: this.$t('no_nodes_to_arrange'),
          type: "warning",
        });
        return;
      }

      const nodes = [...this.getActiveSubModule.conditions.nodes];

      const connectableNodes = nodes
          .filter(node => node.type !== 'start' && node.type !== 'end')
          .sort((a, b) => a.position.y - b.position.y);

      const startNode = nodes.find(node => node.type === 'start');
      const endNode = nodes.find(node => node.type === 'end');

      if (!startNode || !endNode) {
        this.$snackbar.add({
          text: this.$t('start_or_end_node_missing'),
          type: "error",
        });
        return;
      }

      const estimateNodeHeight = (node) => {
        const baseHeight = 100;

        switch (node.type) {
          case 'question':
            const options = node.data?.content?.options || [];
            const hasOther = node.data?.content?.settings?.other;
            return baseHeight + (options.length * 45) + (hasOther ? 45 : 0) + 60;

          case 'number-question':
          case 'one-way-question':
            return baseHeight + 20;

          case 'answer-condition':
          case 'test-score-condition':
            return baseHeight + 40;

          case 'notification':
          case 'assign':
            return baseHeight + 30;

          case 'part':
            return baseHeight + 10;

          case 'grading-repeat':
          case 'go-to':
            return baseHeight + 25;

          default:
            return baseHeight;
        }
      };

      try {
        const startX = 50;
        const nodeSpacing = 100;
        let currentY = 80;

        startNode.position = {x: startX, y: 20};

        connectableNodes.forEach((node, index) => {
          const nodeHeight = estimateNodeHeight(node);

          let nodeX = startX + 300;

          node.position = {x: nodeX, y: currentY};

          currentY += nodeHeight + nodeSpacing;
        });

        const lastNodeY = connectableNodes.length > 0
            ? connectableNodes[connectableNodes.length - 1].position.y + estimateNodeHeight(connectableNodes[connectableNodes.length - 1])
            : 80;

        endNode.position = {x: startX, y: lastNodeY + 150};

        this.setNodes([startNode, endNode, ...connectableNodes]);

        await this.$nextTick();
        this.refreshInternals();

      } catch (error) {
        this.$snackbar.add({
          text: this.$t('error_arranging_nodes'),
          type: "error",
        });
      }
    },
  },
  computed: {
    getName() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user.name;
    },
    orderedParts() {
      try {
        return this.programData.modules
            .flatMap(module => module.subModules)
            .filter(subModule => subModule.order === this.activeSubModule)[0].parts.slice().sort((a, b) => a.order - b.order);
      } catch (e) {
        return [];
      }
    },
    getActiveSubModule() {
      try {
        return this.programData.modules
            .flatMap(module => module.subModules)
            .filter(subModule => subModule.order === this.activeSubModule)[0];
      } catch (e) {
        return {};
      }
    },
  },

}
</script>

<style scoped>
.add-program-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
}


.flow-button {
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  background: #fff;
  border-radius: 16px;
  margin-top: 10px;
  padding: 8px 17px;
  color: #1C3CD5;
  font-size: 14px;
  font-family: 'euclid_medium', sans-serif;
  cursor: pointer;
  border: 1px solid #1C3CD5;
  outline: none;
  align-items: center;
}

.sidebar {
  width: 20%;
  height: calc(100vh - 64px);
  background-color: #FFFFFF;
  border-right: 1px solid #EAECF0;
  padding: 20px;
  overflow-y: auto;
}

.content {
  width: 100%;
  height: calc(100vh - 64px);
  overflow-y: auto;
  padding: 40px;
}

.welcome-title {
  font-family: euclid_medium, sans-serif;
  font-size: 28px;
  color: #101828;
  margin: 0 0 10px 0;
  padding: 0;
}

.welcome-text {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #98A2B3;
  margin: 0 0 20px 0;
  padding: 0;
}

.program-tab-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
}

.program-tab-panel-left {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.program-tab-panel-right {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.program-tab-pane {
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #667085;
  border-bottom: 2px solid transparent;
  cursor: pointer;
}

.program-tab-pane.active {
  border-bottom: 2px solid #4A68F8;
  color: #4A68F8;
}

.content-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-top: 20px;
}

.content-wrapper .content-left {
  width: 70%;
  display: flex;
  flex-direction: column;
  gap: 20px;

}

.content-wrapper .content-right {
  width: 30%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.content-wrapper .content-full {
  width: 100%;
}

.card-wrapper {
  position: relative;
  background-color: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 16px;
  padding: 20px 20px 20px 28px;
  display: flex;
  flex-direction: column;
}

.card-wrapper h3 {
  margin-bottom: 20px;
}

.title-card-border {
  position: absolute;
  height: 100%;
  width: 8px;
  top: 0;
  left: 0;
  background-color: #1C3CD5;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.title-input {
  border: none;
  outline: none;
  font-family: euclid_medium, sans-serif;
  font-size: 24px;
  background-color: transparent;
  color: #040C2D;
}

.description-input {
  border: none;
  outline: none;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  background-color: transparent;
  color: #98A2B3;
  margin-top: 15px;
}

.title-input:focus, .description-input:focus {
  border-bottom: 1px solid #D0D5DD;
}

.card-row {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  gap: 15px;
}

.input-title {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #010511;
  display: block;
  margin-bottom: 5px;
}

.dropdown {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: #F5F5F5;
  border-radius: 8px;
  cursor: pointer;
}

.dropdown img {
  width: 20px;
  height: 20px;
}

.dropdown .check {
  height: 18px;
  width: 18px;
  border-radius: 0;
}

.row-50 {
  width: 100%;
}

.dropdown-content {
  position: absolute;
  top: 60px;
  left: 0;
  width: 100%;
  background-color: #FFFFFF;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 10px;
  z-index: 99999;
}

.dropdown-search {
  width: 100%;
  border-radius: 24px;
  background-color: #f5f5f5;
  border: none;
  outline: none;
  padding: 10px 20px;
  font-size: 14px;
}

.programManager-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 200px;
  overflow-y: auto;
}

.tags-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 5px;
  margin-right: 5px;
  max-height: 200px;
  overflow-y: auto;
}

.programManager-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #000000;
  padding: 10px;
}

.programManager-item:hover {
  background-color: #F5F5F5;
}

.programManager-item img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.bottom-actions {
  margin-top: 15px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
}

.port-list {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.question-block-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  margin-bottom: 5px;
}

.question-block-col {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.question-block {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
  background-color: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 12px;
}

.question-block.error {
  border: 1px solid #FF0000;
  background-color: #FFEBEB;
}

.question-block.warning {
  border: 1px solid #FFA500;
  background-color: #FFF3E0;
}

.question-block .error-icon {
  display: none;
}


.question-block.error .error-icon {
  display: block;
}

.question-block.error .check-icon {
  display: none;
}


.question-number {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #040C2D;
}

h3 {
  padding: 0;
  margin: 0;
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
}

.module-block {
  background-color: #FFFFFF;
  border: 1px solid #D0D5DD;
  border-radius: 8px;
  padding: 15px;
  margin-top: 25px;
  cursor: move;
}

.module-block-list {
  display: flex;
  flex-direction: column;
  position: relative;
}

.module-name-row::before,
.module-name-row::after {
  content: "";
  position: absolute;
  background-color: rgba(28, 60, 213, 0.4);
}

.module-name-row::before {
  top: 50%;
  left: 0;
  width: 10px;
  height: 1.5px;
  transform: translateY(-50%);
}

.module-name-row::after {
  top: 0;
  left: 0;
  width: 1.5px;
  height: 100%;
}

.module-name-row:last-child::after {
  height: 50%;
}

.module-block-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.module-block-body {
  padding-top: 20px;
}

.module-block-icon {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.module-block-arrow {
  height: 20px;
  width: 20px;
  transition: transform 0.2s;
}

.module-block-arrow.rotate {
  transform: rotate(180deg);
}

.module-name-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 10px 15px;
  cursor: pointer;
  position: relative;
}

.module-name-row .module-name {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: rgba(28, 60, 213, 0.4);
}

.module-name-row.active .module-name {
  color: #1C3CD5;
}

.module-step {
  font-size: 12px;
  color: #667085;
  white-space: nowrap;
}

.module-name-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid transparent;
  outline: none;
  background-color: transparent;
  flex: 1;
  margin-right: 5px;
}

.module-name-input:focus {
  outline: none;
  border-bottom: 1px solid #D0D5DD;
}

.node-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.node-button {
  background-color: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.node-button:hover {
  background-color: #F5F5F5;
}

.node-button img {
  width: 20px;
  height: 20px;
}

.node-button span {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #667085;
}
</style>

<style>
.v-tour__target--highlighted {
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.5) !important;
  border-radius: 4px;
}

.v-step {
  background-color: #0B1242 !important;
  color: white !important;
  border-radius: 15px !important;
  font-family: 'euclid_medium', sans-serif;
}

.v-step__button {
  background-color: #0B1242 !important;
  color: white !important;
}

.v-step__button:hover {
  color: #0B1242 !important;
  background-color: white !important;
}

.v-step__arrow {
  color: #0B1242 !important;
}
</style>
